"use client";

import * as React from "react";
import { LucideIcon, Camera, Settings, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

// ============================================================================
// HeroHeader - Beautiful gradient headers for pages
// ============================================================================

export interface HeroHeaderProps {
  /** Title text */
  title: string;
  /** Subtitle text */
  subtitle?: string;
  /** Color variant for the gradient */
  variant?: "emerald" | "blue" | "purple" | "amber" | "rose" | "slate";
  /** Avatar props */
  avatar?: {
    src?: string;
    fallback: string;
    editable?: boolean;
    onEdit?: () => void;
  };
  /** Status indicator */
  status?: {
    online?: boolean;
    verified?: boolean;
  };
  /** Metadata items (icon + text pairs) */
  metadata?: Array<{
    icon: LucideIcon;
    text: string;
  }>;
  /** Badges to display */
  badges?: Array<{
    text: string;
    variant?: "default" | "secondary" | "outline";
  }>;
  /** Primary action button */
  action?: {
    icon?: LucideIcon;
    label: string;
    onClick: () => void;
  };
  /** Additional className */
  className?: string;
  /** Children for custom content */
  children?: React.ReactNode;
}

const gradientVariants = {
  emerald: "from-emerald-600 via-teal-600 to-cyan-600",
  blue: "from-blue-600 via-indigo-600 to-violet-600",
  purple: "from-purple-600 via-violet-600 to-fuchsia-600",
  amber: "from-amber-500 via-orange-500 to-yellow-500",
  rose: "from-rose-600 via-pink-600 to-fuchsia-600",
  slate: "from-slate-700 via-slate-800 to-slate-900",
};

export function HeroHeader({
  title,
  subtitle,
  variant = "emerald",
  avatar,
  status,
  metadata,
  badges,
  action,
  className,
  children,
}: HeroHeaderProps) {
  return (
    <div className={cn(
      "relative overflow-hidden rounded-2xl p-6 md:p-8",
      `bg-gradient-to-br ${gradientVariants[variant]}`,
      className
    )}>
      {/* Decorative background patterns */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full blur-2xl transform -translate-x-1/2 translate-y-1/2" />
      </div>

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
      }} />
      
      <div className="relative flex flex-col md:flex-row md:items-center gap-6">
        {/* Avatar Section */}
        {avatar && (
          <div className="relative group">
            <Avatar className="h-24 w-24 md:h-28 md:w-28 border-4 border-white/30 shadow-xl">
              <AvatarImage src={avatar.src} alt={title} />
              <AvatarFallback className="bg-white/20 text-white text-2xl md:text-3xl font-bold backdrop-blur-sm">
                {avatar.fallback}
              </AvatarFallback>
            </Avatar>
            
            {/* Camera overlay for avatar */}
            {avatar.editable && (
              <button 
                className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                onClick={avatar.onEdit}
                aria-label="Edit avatar"
              >
                <Camera className="h-6 w-6 text-white" />
              </button>
            )}

            {/* Online indicator */}
            {status?.online && (
              <div className="absolute bottom-1 right-1 w-5 h-5 bg-green-400 border-2 border-white rounded-full" />
            )}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 text-white">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <h1 className="text-2xl md:text-3xl font-bold">
              {title}
            </h1>
            {status?.verified && (
              <svg className="h-5 w-5 text-cyan-300" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            )}
          </div>
          
          {subtitle && (
            <p className="text-white/80 text-sm md:text-base mb-3">
              {subtitle}
            </p>
          )}

          {/* Metadata */}
          {metadata && metadata.length > 0 && (
            <div className="flex flex-wrap gap-4 text-sm text-white/70 mb-4">
              {metadata.map((item, index) => (
                <div key={index} className="flex items-center gap-1.5">
                  <item.icon className="h-4 w-4" />
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          )}

          {/* Badges */}
          {badges && badges.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {badges.map((badge, index) => (
                <Badge 
                  key={index} 
                  variant={badge.variant || "secondary"}
                  className="bg-white/20 text-white border-white/20 hover:bg-white/30"
                >
                  {badge.text}
                </Badge>
              ))}
            </div>
          )}

          {/* Custom children content */}
          {children}
        </div>

        {/* Action Button */}
        {action && (
          <div className="md:self-start">
            <Button 
              variant="glass" 
              size="sm"
              onClick={action.onClick}
              className="border border-white/30"
            >
              {action.icon && <action.icon className="h-4 w-4 mr-2" />}
              {action.label}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// PageHeader - Simpler page header without gradient
// ============================================================================

export interface PageHeaderProps {
  title: string;
  description?: string;
  icon?: LucideIcon;
  action?: React.ReactNode;
  breadcrumbs?: Array<{
    label: string;
    href?: string;
  }>;
  className?: string;
}

export function PageHeader({
  title,
  description,
  icon: Icon,
  action,
  breadcrumbs,
  className,
}: PageHeaderProps) {
  return (
    <div className={cn("space-y-2", className)}>
      {/* Breadcrumbs */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav className="flex items-center gap-1 text-sm text-muted-foreground">
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={index}>
              {index > 0 && <ChevronRight className="h-4 w-4" />}
              {crumb.href ? (
                <a href={crumb.href} className="hover:text-foreground transition-colors">
                  {crumb.label}
                </a>
              ) : (
                <span className="text-foreground">{crumb.label}</span>
              )}
            </React.Fragment>
          ))}
        </nav>
      )}
      
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
            {Icon && <Icon className="h-7 w-7 text-muted-foreground" />}
            {title}
          </h1>
          {description && (
            <p className="text-muted-foreground mt-1">{description}</p>
          )}
        </div>
        {action && (
          <div className="flex-shrink-0">{action}</div>
        )}
      </div>
    </div>
  );
}

